import React, { useMemo } from "react"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { exists } from "shared/lib/exists"
import { formatNumber } from "shared/lib/priceUtils"
import { WithClassName } from "shared/types/WithClassName"
import { NumberInput } from "shared/ui/input/number/NumberInput"

import { ProjectFormData } from "features/project-management/types/ProjectFormData"

import { useProjectFormContext } from "../contexts/ProjectFormContext"

interface NumberInputRootProps {
  className?: string
}

interface UseInflationSurchargeWarningParams {
  inflationSurcharge?: number | null
  standardInflationSurcharge?: number | null
}

const FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}

const useInflationSurchargeWarning = ({
  inflationSurcharge,
  standardInflationSurcharge
}: UseInflationSurchargeWarningParams): string | undefined => {
  const { t } = useTranslation()

  const warningMessage = useMemo(() => {
    if (!exists(standardInflationSurcharge)) return undefined
    return (inflationSurcharge ?? 0) !== standardInflationSurcharge
      ? t("Validation.NonStandardInflationSurchargeWarning", {
          percentage: formatNumber({
            number: standardInflationSurcharge,
            options: { maximumFractionDigits: 2 }
          })
        })
      : undefined
  }, [inflationSurcharge, standardInflationSurcharge, t])

  return warningMessage
}

interface ProjectFormInflationSurchargeReadonlyProps extends WithClassName {
  standardInflationSurcharge?: number | null
}

const ProjectFormInflationSurchargeReadonly = React.memo(
  ({
    standardInflationSurcharge,
    className
  }: ProjectFormInflationSurchargeReadonlyProps) => {
    const { t } = useTranslation()
    const inflationSurcharge = useWatch<
      ProjectFormData,
      "project.inflationSurcharge"
    >({
      name: "project.inflationSurcharge"
    })

    const warningMessage = useInflationSurchargeWarning({
      inflationSurcharge,
      standardInflationSurcharge
    })

    return (
      <NumberInput.Root
        label={t("Price.Surcharge")}
        data-testid="project__inflation-surcharge"
        className={classNames("project-form__inflation-surcharge", className)}
        readonly
        format={FORMAT_OPTIONS}
      >
        <NumberInput.LabelContainer>
          <NumberInput.Label />
          <NumberInput.InfoTip
            content={warningMessage}
            variant="warning"
            label={t("Form.ShowInfoFor", { field: t("Price.Surcharge") })}
            className="input__info-tip--warning"
          />
        </NumberInput.LabelContainer>
        <NumberInput.Readonly
          value={inflationSurcharge}
          render={value => `${value} %`}
        />
        <NumberInput.ErrorMessage />
      </NumberInput.Root>
    )
  }
)

interface ProjectFormInflationSurchargeEditableProps
  extends WithClassName,
    Partial<Omit<NumberInputRootProps, "name" | "label">> {
  standardInflationSurcharge?: number | null
}

const ProjectFormInflationSurchargeEditable = React.memo(
  ({
    className,
    standardInflationSurcharge,
    ...rest
  }: ProjectFormInflationSurchargeEditableProps) => {
    const { t } = useTranslation()
    const { isReadonly } = useProjectFormContext()
    const inflationSurcharge = useWatch<
      ProjectFormData,
      "project.inflationSurcharge"
    >({
      name: "project.inflationSurcharge"
    })

    const warningMessage = useInflationSurchargeWarning({
      inflationSurcharge,
      standardInflationSurcharge
    })

    return (
      <NumberInput.Root
        name="project.inflationSurcharge"
        label={t("Price.Surcharge")}
        data-testid="project__inflation-surcharge"
        className={classNames("project-form__inflation-surcharge", className)}
        hasWarning={!!warningMessage}
        {...rest}
        readonly={isReadonly}
        step={0.1}
        format={FORMAT_OPTIONS}
      >
        <NumberInput.LabelContainer>
          <NumberInput.ScrubArea>
            <NumberInput.ScrubAreaCursor />
            <NumberInput.Label />
          </NumberInput.ScrubArea>
          <NumberInput.InfoTip
            content={warningMessage}
            variant="warning"
            label={t("Form.ShowInfoFor", { field: t("Price.Surcharge") })}
            className="input__info-tip--warning"
          />
        </NumberInput.LabelContainer>

        <NumberInput.Group>
          <NumberInput.Decrement />
          <NumberInput.Focus>
            <NumberInput.Input />
            <NumberInput.Affix>%</NumberInput.Affix>
          </NumberInput.Focus>
          <NumberInput.Increment />
        </NumberInput.Group>
        <NumberInput.Readonly render={value => `${value} %`} />
        <NumberInput.ErrorMessage />
      </NumberInput.Root>
    )
  }
)

export interface ProjectFormInflationSurchargeProps
  extends WithClassName,
    Partial<Omit<NumberInputRootProps, "name" | "label">> {
  children?: React.ReactNode
}

export const ProjectFormInflationSurcharge = React.memo(
  ({ children, className, ...rest }: ProjectFormInflationSurchargeProps) => {
    const { isLoading, isReadonly, model } = useProjectFormContext()
    const standardInflationSurcharge =
      model?.pricing.data.standardInflationSurcharge?.data

    if (isLoading) {
      return null
    }

    if (!isReadonly) {
      return (
        <ProjectFormInflationSurchargeEditable
          className={className}
          standardInflationSurcharge={standardInflationSurcharge}
          {...rest}
        />
      )
    }

    return (
      <ProjectFormInflationSurchargeReadonly
        className={className}
        standardInflationSurcharge={standardInflationSurcharge}
      />
    )
  }
)

ProjectFormInflationSurcharge.displayName = "ProjectFormInflationSurcharge"
